import './payment-method-add-sheet.scss';

import Script from 'next/script';
import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { AddressSelectSupporter } from '@sola/ark-core/molecules/address-select/address-select-supporter.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.js';
import {
  FormSubmit,
  FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.js';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { AddressSelectOptions } from '@sola/schema/dotcom/shared/address.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  PaymentMethodTypesEnum,
  SupporterAchDetails,
  SupporterCCDetails,
  SupporterCCFieldsEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.js';
import { userErrorPaymentMethodCreate } from '@sola/shared-utils/errors/supporters.js';
import {
  isPaymentMethodAch,
  isPaymentMethodCard,
} from '@sola/shared-utils/my/payment-method-type-guards.js';
import {
  usePaymentMethodAdd,
  useSupporterBillingAddresses,
} from '@sola/supporter-sdk/hooks/payment-methods.js';
import { getRecurringCommitments } from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentAvatarGroup } from '../../molecules/commitment-avatar-group/commitment-avatar-group';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import { PaymentACHFields } from '../../molecules/payment-method-add-form/payment-ach-fields';
import { PaymentCCFields } from '../../molecules/payment-method-add-form/payment-cc-fields';
import { PaymentMethodAddForm } from '../../molecules/payment-method-add-form/payment-method-add-form';
import { PaymentMethodSwitch } from '../../molecules/payment-method-switch/payment-method-switch';
import { SectionContent } from '../../molecules/section-content/section-content';
import { LocalAlertConfig } from '../../types/localeAlerts';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';

export interface PaymentMethodAddSheetCommitmentListProps {
  commitments?: GraphQLCommitment[];
}

export const PaymentMethodAddSheetCommitmentList: React.FC<
  PaymentMethodAddSheetCommitmentListProps
> = ({ commitments }) => {
  const { t } = useTranslation('my');

  if (!commitments) {
    return;
  }

  return (
    <div className="PaymentMethodAddSheet__commitment-badges flex flex-col lg:flex-row md:flex-row w-full lg:items-center md:items-center gap-comfy-sm justify-between">
      <h3 className="body-sm">{t('addPaymentMethod.commitmentSummary')}</h3>

      <CommitmentAvatarGroup
        commitments={commitments}
        layout="spaced"
        tooltipMessage={t('payments.methodCommitmentsTooltip', {
          count: commitments.length,
        })}
      />
    </div>
  );
};

type PaymentMethodAddSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const AddPaymentMethodSheetFooter: React.FC<
  PaymentMethodAddSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('my');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            fullWidth
          >
            <button type="button">
              {t('addPaymentMethod.sheetAction.cancel')}
            </button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('addPaymentMethod.sheetAction.add')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodAddSheetProps {
  commitments?: GraphQLCommitment[];
  /**
   * This should only take in elements that resolve to html buttons.
   */
  openTrigger: ReactElement;
}
export const PaymentMethodAddSheet: React.FC<PaymentMethodAddSheetProps> = ({
  openTrigger,
  commitments = [],
}) => {
  const { t } = useTranslation('my');
  const title = t('addPaymentMethod.form.title');
  const [isOpen, setIsOpen] = useState(false);
  const [sheetAlerts, setSheetAlerts] = useState<LocalAlertConfig[]>([]);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypesEnum>(
    PaymentMethodTypesEnum.Ach,
  );
  const recurringCommitments = getRecurringCommitments(commitments);
  const addPaymentMethod = usePaymentMethodAdd();
  const { supporterAddresses } = useSupporterBillingAddresses();

  const removeSheetAlert = (id: string) =>
    setSheetAlerts(sheetAlerts.filter((alert) => alert.id !== id));

  const handleSubmit = async (
    paymentMethodDetails: SupporterPaymentMethodDetails,
  ) => {
    setSheetAlerts([]);
    clearAlerts();

    try {
      const addedPaymentMethod =
        await addPaymentMethod.mutateAsync(paymentMethodDetails);

      if (!addedPaymentMethod) {
        throw userErrorPaymentMethodCreate();
      }

      addAlerts(
        createPaymentMethodSuccessAlert(
          addedPaymentMethod,
          'addPaymentMethod.success.action',
        ),
      );
      setIsOpen(false);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodCreate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  const handleAchSubmit = async (data: FieldValues) => {
    const achDetails = data as SupporterAchDetails;
    await handleSubmit({ ...achDetails, type: PaymentMethodTypesEnum.Ach });
  };

  const handleCardSubmit = async (data: FieldValues) => {
    const { addressOption, ...rest } = data as SupporterCCDetails;
    let cardDetails = rest;
    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    if (addressOption !== AddressSelectOptions.Add) {
      const supporterAddress = supporterAddresses?.get(addressOption);
      cardDetails = {
        ...cardDetails,
        ...supporterAddress?.billingAddress,
      };
    }

    await handleSubmit({
      ...cardDetails,
      addressOption,
      expirationDate,
      type: PaymentMethodTypesEnum.Card,
    });
  };

  return (
    <>
      <Script src="/tokenex.js" strategy="lazyOnload" />
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger>{openTrigger}</SheetTrigger>
        <SheetContent title={title}>
          <SheetHeader>{title}</SheetHeader>
          <SheetBody>
            <p
              id="selectPaymentMethodType"
              className="body-sm md:body-md body-neutral-mild"
            >
              {t('addPaymentMethod.form.leadin')}
            </p>

            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={() => removeSheetAlert(sheetAlert.id)}
                  />
                ))}
              </div>
            )}

            <form aria-describedby="selectPaymentMethodType">
              <PaymentMethodSwitch
                setSelectedPaymentMethod={setSelectedMethod}
              />
            </form>

            <div className={isPaymentMethodAch(selectedMethod) ? '' : 'hidden'}>
              <Alert
                variant="positive"
                title={t('addPaymentMethod.alert.title')}
                message={t('addPaymentMethod.alert.message')}
              />

              <Divider purpose="neutral" className="my-comfy-xl" />

              <PaymentMethodAddForm onSubmit={handleAchSubmit}>
                <SectionContent
                  heading={t('addPaymentMethod.form.formTitle.bank')}
                >
                  <Fieldset id="ach-fields">
                    <PaymentACHFields />
                  </Fieldset>
                </SectionContent>

                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>

            <div
              className={isPaymentMethodCard(selectedMethod) ? '' : 'hidden'}
            >
              <Divider purpose="neutral" className="my-comfy-xl" />
              <PaymentMethodAddForm onSubmit={handleCardSubmit}>
                <div>
                  <SectionContent
                    heading={t('addPaymentMethod.form.formTitle.card')}
                  >
                    <Fieldset id="cc-fields">
                      <PaymentCCFields />
                    </Fieldset>
                  </SectionContent>

                  <Divider purpose="neutral" className="my-comfy-xl" />

                  <SectionContent
                    heading={t(
                      'updatePaymentMethod.form.formTitle.billingInfo',
                    )}
                  >
                    <AddressSelectSupporter
                      label={t('updatePaymentMethod.form.fields.billingInfo')}
                    />
                  </SectionContent>
                </div>
                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>

            <Divider purpose="neutral" className="my-comfy-xl" />

            {!!recurringCommitments?.length && (
              <PaymentMethodAddSheetCommitmentList
                commitments={recurringCommitments}
              />
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
};
